import { useMemo, useState } from 'react';
import styled from 'styled-components';

import Search from 'components/shared/Search';
import useNow from 'hooks/useNow';
import useWorkflow from 'hooks/useWorkflow';
import { elapsed } from 'lib/date';
import Workflow from 'models/Workflow';
import { colors } from 'styles/theme/colors';

import DetailSlideout from './DetailSlideout';
import { sentToSummary } from './ReferralSentTo';
import { Pill, Tone } from './StatusBadge';

type SortDirection = 'asc' | 'desc';

type FacilityStatus = 'accepted' | 'denied' | 'pending';

const FACILITY_STATUS_DISPLAY: Record<FacilityStatus, { label: string; tone: Tone }> = {
  accepted: { label: 'Accepted', tone: 'success' },
  denied: { label: 'Denied', tone: 'danger' },
  pending: { label: 'Pending Decision', tone: 'neutral' },
};

// Reads the Facility Decision step's outcome for one SNF. Returns the determination value
// ('accepted'/'denied') when the action is completed, 'pending' otherwise.
function facilityDecisionStatus(workflow: Workflow, groupId: string): FacilityStatus {
  const action = workflow.actions.find((a) => a.kind === 'FacilityDecision' && a.groupId === groupId);
  if (!action?.isCompleted) return 'pending';
  const determination = workflow.valueFor('facility_determination', action)?.value;
  if (determination === 'accepted') return 'accepted';
  if (determination === 'denied') return 'denied';
  return 'pending';
}

export default function SNFSlideout({
  open,
  workflow,
  onClose,
}: {
  open: boolean;
  workflow: Workflow;
  onClose: () => void;
}) {
  const [search, setSearch] = useState('');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  // Gated on `open` — SNFCell keeps this mounted while closed (for the Foldout animation), so an
  // ungated tick would re-render every multi-SNF row's hidden table every 30s.
  const now = useNow(30_000, open);

  // The list-page workflow carries a subset of values — facility_determination is only present
  // in the detail response. Fetch the full workflow when the slideout opens so status reads are
  // accurate; fall back to the prop while the request is in flight.
  const { workflow: fullWorkflow } = useWorkflow(open ? workflow.id : undefined);
  const detailWorkflow = fullWorkflow ?? workflow;

  const rows = useMemo(
    () =>
      workflow.groups.map((group) => ({
        group,
        status: facilityDecisionStatus(detailWorkflow, group.id),
        createdAt: workflow.createdAt,
      })),
    [workflow, detailWorkflow]
  );

  const visible = useMemo(() => {
    const query = search.trim().toLowerCase();
    const filtered = query ? rows.filter((r) => r.group.name.toLowerCase().includes(query)) : rows;
    return [...filtered].sort((a, b) => {
      const cmp = a.group.name.localeCompare(b.group.name);
      return sortDirection === 'asc' ? cmp : -cmp;
    });
  }, [rows, search, sortDirection]);

  const subtitle = `Referral sent to ${sentToSummary(workflow.groups)}`;

  return (
    <DetailSlideout open={open} hideBackButton onClose={onClose} title={workflow.patientName} subtitle={subtitle}>
      <Controls>
        <SearchWrap>
          <Search value={search} onChange={setSearch} placeholder='Search' />
        </SearchWrap>
      </Controls>
      <Table>
        <thead>
          <tr>
            <ColHeader $sortable onClick={() => setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'))}>
              SNF <SortArrow>{sortDirection === 'asc' ? '↑' : '↓'}</SortArrow>
            </ColHeader>
            <ColHeader>Status</ColHeader>
            <ColHeader>Time Elapsed</ColHeader>
          </tr>
        </thead>
        <tbody>
          {visible.map(({ group, status, createdAt }) => {
            const display = FACILITY_STATUS_DISPLAY[status];
            return (
              <Tr key={group.id}>
                <SnfTd>{group.name}</SnfTd>
                <Td>
                  <StatusPill $tone={display.tone}>{display.label}</StatusPill>
                </Td>
                <Td>{createdAt ? elapsed(createdAt, now) : '—'}</Td>
              </Tr>
            );
          })}
          {visible.length === 0 && (
            <tr>
              <EmptyCell colSpan={3}>No SNFs match your search.</EmptyCell>
            </tr>
          )}
        </tbody>
      </Table>
    </DetailSlideout>
  );
}

const Controls = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SearchWrap = styled.div`
  width: 320px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  cursor: default;
`;

const ColHeader = styled.th<{ $sortable?: boolean }>`
  text-align: left;
  padding: 10px 12px;
  font-size: 12px;
  font-weight: 500;
  color: ${colors.black50};
  border-bottom: 1px solid ${colors.black10};
  white-space: nowrap;
  cursor: ${({ $sortable }) => ($sortable ? 'pointer' : 'default')};
  user-select: none;

  &:hover {
    color: ${({ $sortable }) => ($sortable ? colors.black100 : colors.black50)};
  }
`;

const SortArrow = styled.span`
  margin-left: 4px;
  font-size: 11px;
`;

const Tr = styled.tr`
  & + & td {
    border-top: 1px solid ${colors.black10};
  }
`;

const Td = styled.td`
  padding: 12px;
  vertical-align: middle;
`;

const SnfTd = styled(Td)`
  font-weight: 500;
`;

const StatusPill = styled(Pill)`
  font-size: 11px;
  padding: 3px 10px;
`;

const EmptyCell = styled.td`
  padding: 20px 12px;
  color: ${colors.black50};
  font-size: 12px;
`;
